import type {SelectOptionItem} from '@yandex-delivery/blocks'
import {Button, Grid, List, Select} from '@yandex-delivery/blocks'
import {useController, useFormContext} from '@yandex-delivery/form'
import {get, noop} from '@yandex-delivery/utils'
import clsx from 'clsx'
import React, {useCallback, useMemo} from 'react'

import type {PickupStation} from 'src/shared/types'

import {classes} from './PointSelect.classes'
import type {PointSelectProps} from './PointSelect.types'
import {filter, getFilterLabel, getLabel} from './PointSelect.utils'
import {PvzIcon} from './PvzIcon'

function renderItem(item: SelectOptionItem<PickupStation>) {
  return (
    <Grid alignItems="center" size="s" wrap="nowrap" container>
      <Grid item>
        <PvzIcon />
      </Grid>
      <Grid size="s" container item xs>
        <Grid item xs>
          <List.Label variant="body2">
            <span className={classes.label}>{getLabel(item)}</span>
          </List.Label>
          <List.Description color="minor" variant="caption1">
            {item.value.name}
          </List.Description>
        </Grid>
      </Grid>
    </Grid>
  )
}

export function PointSelect({
  name,
  'data-testid': dataTestId,
  points,
  placeholder,
  trailButtonCaption,
  isPending = false,
  isMobile = false,
  isDisabled = false,
  onShow = noop,
}: PointSelectProps): React.ReactElement {
  const {control} = useFormContext()
  const {field, fieldState} = useController({name, control})

  const selectHandler = useCallback(
    (item: SelectOptionItem<PickupStation> | null) => {
      field.onChange(item?.value)
    },
    [field],
  )

  const preparedPoints = useMemo<SelectOptionItem<PickupStation>[]>(() => {
    if (points === undefined) {
      return []
    }

    return points.map<SelectOptionItem<PickupStation>>(point => ({
      label: getFilterLabel(point),
      value: point,
    }))
  }, [points])

  return (
    <div ref={field.ref}>
      <Select<PickupStation>
        className={clsx(classes.select, {[classes.mobile]: isMobile})}
        data-testid={dataTestId}
        disabled={isDisabled}
        error={fieldState.invalid}
        filter={filter}
        helperText={fieldState.error?.message}
        hideToggleIcon={!isMobile}
        isPending={isPending}
        makeKey={get('value.id')}
        makeText={getLabel}
        name={name}
        onChange={selectHandler}
        options={preparedPoints}
        placeholder={placeholder}
        portal={false}
        renderOptionText={renderItem}
        trail={
          isMobile ? null : (
            <Button
              className={classes.button}
              disabled={preparedPoints.length === 0}
              loading={isPending}
              onPress={onShow}
              shape="circle"
              size="xs"
              variant="outline"
            >
              {trailButtonCaption}
            </Button>
          )
        }
        value={field.value}
      />
    </div>
  )
}
